/**
 * Matching Criteria Types
 * TypeScript definitions for filtering and customizing matches
 * 
 * This file mirrors the backend MatchingCriteria.java DTO structure
 * to ensure consistency between frontend and backend filtering logic.
 * 
 * @see /backend-java/src/main/java/com/financialinsights/dto/MatchingCriteria.java
 * @author Financial Insights Platform
 * @version 1.0
 */

import { 
  RiskTolerance, 
  InvestmentStrategy, 
  ResearchStyle,
  Sector 
} from "../utils/financialTaxonomy";

/**
 * Main Matching Criteria Interface
 * Used for filtering matches in the Match tab and API requests
 */
export interface MatchingCriteria {
  // General Filters
  role?: "advisor" | "investor" | "analyst" | string;
  minYearsExperience?: number;
  maxYearsExperience?: number;
  requiredCertifications?: string[];
  preferredCertifications?: string[];
  location?: string;
  minAUM?: number;
  maxAUM?: number;
  minRating?: number;
  
  // Investor-specific Criteria
  specializations?: string[];
  riskTolerance?: RiskTolerance;
  serviceNeeds?: string[];
  sectors?: string[];
  investmentStrategy?: InvestmentStrategy;
  researchStyle?: ResearchStyle;
  
  // Analyst-specific Criteria
  minPublications?: number;
  researchFocusAreas?: string[];
  
  // Search Parameters
  maxResults?: number;
  minMatchScore?: number;
  includeLowScores?: boolean;
  sortBy?: SortOption;
  sortOrder?: SortOrder;
  
  // UI-specific Filters (not sent to backend)
  customLocation?: string;
  selectedSectors?: string[];
  selectedSpecializations?: string[];
  selectedCertifications?: string[];
}

/**
 * Sort Options for Match Results
 */
export type SortOption = 
  | "score"           // Sort by match score (default)
  | "experience"      // Sort by years of experience
  | "alphabetical"    // Sort by name A-Z
  | "rating"          // Sort by user rating
  | "aum"             // Sort by AUM (advisors only)
  | "publications"    // Sort by publications (analysts only)
  | "newest"          // Sort by account creation date
  | "reviewCount";    // Sort by number of reviews

export type SortOrder = "asc" | "desc";

/**
 * Match Filter Presets
 * Common filter configurations for quick selection
 */
export interface FilterPreset {
  id: string;
  name: string;
  description: string;
  criteria: MatchingCriteria;
}

/**
 * Default Matching Criteria
 * Used when no filters are applied
 */
export const DEFAULT_MATCHING_CRITERIA: MatchingCriteria = {
  maxResults: 50,
  minMatchScore: 70,
  includeLowScores: false,
  sortBy: "score",
  sortOrder: "desc",
};

/**
 * Strict Matching Criteria
 * High-quality matches only
 */
export const STRICT_MATCHING_CRITERIA: MatchingCriteria = {
  maxResults: 20,
  minMatchScore: 85,
  minRating: 4.0,
  includeLowScores: false,
  sortBy: "score",
  sortOrder: "desc",
};

/**
 * Relaxed Matching Criteria
 * More inclusive matching
 */
export const RELAXED_MATCHING_CRITERIA: MatchingCriteria = {
  maxResults: 100,
  minMatchScore: 50,
  includeLowScores: true,
  sortBy: "score",
  sortOrder: "desc",
};

/**
 * Common Filter Presets
 */
export const FILTER_PRESETS: FilterPreset[] = [
  {
    id: "default",
    name: "Recommended Matches",
    description: "Best matches based on your profile (70% or higher)",
    criteria: DEFAULT_MATCHING_CRITERIA,
  },
  {
    id: "strict",
    name: "Top Tier Only",
    description: "Highest quality matches (85% or higher, 4+ stars)",
    criteria: STRICT_MATCHING_CRITERIA,
  },
  {
    id: "relaxed",
    name: "All Possibilities",
    description: "Cast a wider net (50% or higher)",
    criteria: RELAXED_MATCHING_CRITERIA,
  },
  {
    id: "local",
    name: "Local Professionals",
    description: "Prioritize professionals in your area",
    criteria: {
      ...DEFAULT_MATCHING_CRITERIA,
      sortBy: "score",
    },
  },
  {
    id: "premium",
    name: "Premium Certified",
    description: "CFA or CFP certified professionals only",
    criteria: {
      ...STRICT_MATCHING_CRITERIA,
      requiredCertifications: ["CFA", "CFP"],
    },
  },
  {
    id: "experienced",
    name: "10+ Years Experience",
    description: "Seasoned professionals with extensive track record",
    criteria: {
      ...DEFAULT_MATCHING_CRITERIA,
      minYearsExperience: 10,
      sortBy: "experience",
      sortOrder: "desc",
    },
  },
  {
    id: "engaged",
    name: "Engaged Users",
    description: "Only show users with pending or signed engagement agreements",
    criteria: DEFAULT_MATCHING_CRITERIA,
  },
];

/**
 * Helper Functions for Matching Criteria
 */

/**
 * Create matching criteria from UI filter state
 * Converts React component state to API-compatible MatchingCriteria
 */
export function createMatchingCriteriaFromFilters(filters: {
  minMatchScore: number[];
  selectedSpecializations: string[];
  selectedCertifications: string[];
  selectedSectors: string[];
  selectedLocation: string;
  customLocation?: string;
  minRating: string;
  role?: string;
}): MatchingCriteria {
  return {
    role: filters.role,
    minMatchScore: filters.minMatchScore[0],
    specializations: filters.selectedSpecializations.length > 0 
      ? filters.selectedSpecializations 
      : undefined,
    requiredCertifications: filters.selectedCertifications.length > 0 
      ? filters.selectedCertifications 
      : undefined,
    sectors: filters.selectedSectors.length > 0 
      ? filters.selectedSectors 
      : undefined,
    location: filters.selectedLocation !== "all" && filters.selectedLocation !== "custom"
      ? filters.selectedLocation
      : filters.customLocation || undefined,
    minRating: parseFloat(filters.minRating) || undefined,
    sortBy: "score",
    sortOrder: "desc",
    includeLowScores: filters.minMatchScore[0] < 70,
  };
}

/**
 * Merge multiple criteria objects
 * Later criteria override earlier ones
 */
export function mergeMatchingCriteria(...criteriaList: Partial<MatchingCriteria>[]): MatchingCriteria {
  return criteriaList.reduce((merged, criteria) => {
    return {
      ...merged,
      ...criteria,
      // Special handling for arrays - concatenate instead of replace
      requiredCertifications: [
        ...(merged.requiredCertifications || []),
        ...(criteria.requiredCertifications || []),
      ].filter((v, i, arr) => arr.indexOf(v) === i), // Remove duplicates
      specializations: [
        ...(merged.specializations || []),
        ...(criteria.specializations || []),
      ].filter((v, i, arr) => arr.indexOf(v) === i),
      sectors: [
        ...(merged.sectors || []),
        ...(criteria.sectors || []),
      ].filter((v, i, arr) => arr.indexOf(v) === i),
    };
  }, {} as MatchingCriteria);
}

/**
 * Validate matching criteria
 * Returns array of validation errors, empty if valid
 */
export function validateMatchingCriteria(criteria: MatchingCriteria): string[] {
  const errors: string[] = [];
  
  // Validate match score range
  if (criteria.minMatchScore !== undefined) {
    if (criteria.minMatchScore < 0 || criteria.minMatchScore > 100) {
      errors.push("Match score must be between 0 and 100");
    }
  }
  
  // Validate rating range
  if (criteria.minRating !== undefined) {
    if (criteria.minRating < 0 || criteria.minRating > 5) {
      errors.push("Rating must be between 0 and 5");
    }
  }
  
  // Validate years of experience
  if (criteria.minYearsExperience !== undefined && criteria.minYearsExperience < 0) {
    errors.push("Minimum years of experience cannot be negative");
  }
  
  if (criteria.maxYearsExperience !== undefined && criteria.maxYearsExperience < 0) {
    errors.push("Maximum years of experience cannot be negative");
  }
  
  if (
    criteria.minYearsExperience !== undefined &&
    criteria.maxYearsExperience !== undefined &&
    criteria.minYearsExperience > criteria.maxYearsExperience
  ) {
    errors.push("Minimum years of experience cannot exceed maximum");
  }
  
  // Validate AUM range
  if (criteria.minAUM !== undefined && criteria.minAUM < 0) {
    errors.push("Minimum AUM cannot be negative");
  }
  
  if (criteria.maxAUM !== undefined && criteria.maxAUM < 0) {
    errors.push("Maximum AUM cannot be negative");
  }
  
  if (
    criteria.minAUM !== undefined &&
    criteria.maxAUM !== undefined &&
    criteria.minAUM > criteria.maxAUM
  ) {
    errors.push("Minimum AUM cannot exceed maximum AUM");
  }
  
  // Validate max results
  if (criteria.maxResults !== undefined) {
    if (criteria.maxResults < 1) {
      errors.push("Max results must be at least 1");
    }
    if (criteria.maxResults > 1000) {
      errors.push("Max results cannot exceed 1000");
    }
  }
  
  return errors;
}

/**
 * Check if criteria is empty (no filters applied)
 */
export function isCriteriaEmpty(criteria: MatchingCriteria): boolean {
  const {
    sortBy,
    sortOrder,
    maxResults,
    minMatchScore,
    includeLowScores,
    ...filters
  } = criteria;
  
  return Object.values(filters).every(value => {
    if (value === undefined || value === null) return true;
    if (Array.isArray(value)) return value.length === 0;
    return false;
  });
}

/**
 * Count active filters
 * Returns the number of filters currently applied
 */
export function countActiveFilters(criteria: MatchingCriteria): number {
  let count = 0;
  
  if (criteria.minYearsExperience !== undefined) count++;
  if (criteria.maxYearsExperience !== undefined) count++;
  if (criteria.requiredCertifications?.length) count++;
  if (criteria.preferredCertifications?.length) count++;
  if (criteria.location) count++;
  if (criteria.minAUM !== undefined) count++;
  if (criteria.maxAUM !== undefined) count++;
  if (criteria.minRating !== undefined) count++;
  if (criteria.specializations?.length) count++;
  if (criteria.riskTolerance) count++;
  if (criteria.serviceNeeds?.length) count++;
  if (criteria.sectors?.length) count++;
  if (criteria.investmentStrategy) count++;
  if (criteria.researchStyle) count++;
  if (criteria.minPublications !== undefined) count++;
  if (criteria.researchFocusAreas?.length) count++;
  if (criteria.minMatchScore !== undefined && criteria.minMatchScore !== 70) count++;
  
  return count;
}

/**
 * Reset criteria to defaults
 */
export function resetCriteria(): MatchingCriteria {
  return { ...DEFAULT_MATCHING_CRITERIA };
}

/**
 * Convert criteria to URL query parameters
 * Useful for shareable filter links
 */
export function criteriaToQueryParams(criteria: MatchingCriteria): URLSearchParams {
  const params = new URLSearchParams();
  
  Object.entries(criteria).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    
    if (Array.isArray(value)) {
      if (value.length > 0) {
        params.set(key, value.join(","));
      }
    } else {
      params.set(key, String(value));
    }
  });
  
  return params;
}

/**
 * Parse criteria from URL query parameters
 */
export function criteriaFromQueryParams(params: URLSearchParams): MatchingCriteria {
  const criteria: MatchingCriteria = {};
  
  // Parse numbers
  const numericFields = [
    "minYearsExperience",
    "maxYearsExperience",
    "minAUM",
    "maxAUM",
    "minRating",
    "maxResults",
    "minMatchScore",
    "minPublications",
  ];
  
  numericFields.forEach(field => {
    const value = params.get(field);
    if (value !== null) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        (criteria as any)[field] = num;
      }
    }
  });
  
  // Parse arrays
  const arrayFields = [
    "requiredCertifications",
    "preferredCertifications",
    "specializations",
    "serviceNeeds",
    "sectors",
    "researchFocusAreas",
  ];
  
  arrayFields.forEach(field => {
    const value = params.get(field);
    if (value) {
      (criteria as any)[field] = value.split(",").map(s => s.trim());
    }
  });
  
  // Parse strings
  const stringFields = [
    "role",
    "location",
    "riskTolerance",
    "investmentStrategy",
    "researchStyle",
    "sortBy",
    "sortOrder",
  ];
  
  stringFields.forEach(field => {
    const value = params.get(field);
    if (value) {
      (criteria as any)[field] = value;
    }
  });
  
  // Parse booleans
  const includeLowScores = params.get("includeLowScores");
  if (includeLowScores !== null) {
    criteria.includeLowScores = includeLowScores === "true";
  }
  
  return criteria;
}

/**
 * Format criteria for display
 * Returns human-readable description of active filters
 */
export function formatCriteriaDescription(criteria: MatchingCriteria): string {
  const parts: string[] = [];
  
  if (criteria.minMatchScore) {
    parts.push(`${criteria.minMatchScore}%+ match`);
  }
  
  if (criteria.minRating) {
    parts.push(`${criteria.minRating}+ stars`);
  }
  
  if (criteria.minYearsExperience) {
    parts.push(`${criteria.minYearsExperience}+ years experience`);
  }
  
  if (criteria.requiredCertifications?.length) {
    parts.push(`${criteria.requiredCertifications.join(", ")} certified`);
  }
  
  if (criteria.location) {
    parts.push(`in ${criteria.location}`);
  }
  
  if (criteria.sectors?.length) {
    parts.push(`${criteria.sectors.join(", ")} sectors`);
  }
  
  if (parts.length === 0) {
    return "All matches";
  }
  
  return parts.join(" • ");
}

/**
 * Export for convenience
 */
export default {
  DEFAULT_MATCHING_CRITERIA,
  STRICT_MATCHING_CRITERIA,
  RELAXED_MATCHING_CRITERIA,
  FILTER_PRESETS,
  createMatchingCriteriaFromFilters,
  mergeMatchingCriteria,
  validateMatchingCriteria,
  isCriteriaEmpty,
  countActiveFilters,
  resetCriteria,
  criteriaToQueryParams,
  criteriaFromQueryParams,
  formatCriteriaDescription,
};
